import type { Jurisdiction } from "./api";

// The server normalizes jurisdiction codes with `strip().lower()`; mirror that
// on the client so comparisons against the enabled catalog (which is already
// lowercase) and against the project's current code line up.
function normalizeCode(code: string | null | undefined): string {
  return (code ?? "").trim().toLowerCase();
}

// Jurisdictions the client never offers, even though the backend keeps them
// enabled. `ru` is the server default and stays assignable from the admin
// console; it is simply hidden from the user-facing app.
const HIDDEN_JURISDICTION_CODES = ["ru"];

export function isHiddenJurisdiction(code: string | null | undefined): boolean {
  return HIDDEN_JURISDICTION_CODES.includes(normalizeCode(code));
}

/**
 * Code to send in `POST /api/v1/projects`. Empty selection → undefined, so the
 * field is omitted and the server applies the default jurisdiction. Anything
 * not in the enabled catalog is dropped rather than risking a 400.
 */
export function jurisdictionCreateCode({
  selected,
  enabledCodes,
}: {
  selected: string;
  enabledCodes: string[];
}): string | undefined {
  const code = normalizeCode(selected);
  if (!code) return undefined;
  return enabledCodes.includes(code) ? code : undefined;
}

/**
 * Code to send in `PATCH /api/v1/projects/{id}`. Returns a code only for a real
 * change to an enabled jurisdiction:
 *  - empty selection → undefined (partial update must not clear the value);
 *  - unchanged selection → undefined (no-op);
 *  - a retired code → undefined (sending it would 400), which also covers
 *    re-selecting a retired current value.
 */
export function jurisdictionPatchCode({
  selected,
  current,
  enabledCodes,
}: {
  selected: string;
  current: string | null | undefined;
  enabledCodes: string[];
}): string | undefined {
  const code = normalizeCode(selected);
  if (!code) return undefined;
  if (code === normalizeCode(current)) return undefined;
  return enabledCodes.includes(code) ? code : undefined;
}

/**
 * Options for the jurisdiction select. The catalog only carries enabled
 * jurisdictions, but a project may still point at a retired one; append it so
 * its name renders as the current selection.
 */
export function optionsWithCurrent(
  options: Jurisdiction[],
  current: { code?: string | null; name?: string | null },
): Jurisdiction[] {
  const code = normalizeCode(current.code);
  if (!code) return options;
  // A hidden current jurisdiction (e.g. the default `ru`) must not be re-added
  // to the select — the app deliberately keeps it out of the user's reach.
  if (isHiddenJurisdiction(code)) return options;
  if (options.some((o) => normalizeCode(o.code) === code)) return options;
  return [...options, { code, name: current.name ?? code }];
}
